/**
 * What a character sells.
 *
 * A shop is a handful of numbers and a list, and until now the only way to
 * change either was to stop the server and edit a file. It is edited here the
 * same way a drop table is: the server holds it, the panel asks for it, and
 * every change goes back as a command.
 */

import type { ItemDefinitionWire, QuestWire, ShopStockPacket } from '@inverse/protocol';
import { useState } from 'react';
import type { AdminCommand } from '../Admin';
import { Conditions, type Condition } from './Dialogue';
import { Slider } from './Palette';

/**
 * The conditions a line carries, read from what the server sent.
 *
 * Anything unreadable comes back as none rather than throwing: a panel that
 * cannot draw the list is the panel somebody would have used to fix it.
 */
function whenOf(json: string): Condition[] {
  try {
    const held: unknown = JSON.parse(json || '[]');
    return Array.isArray(held) ? (held as Condition[]) : [];
  } catch {
    return [];
  }
}

export function ShopEditor({
  npcId,
  shop,
  items,
  quests,
  characters,
  objects,
  onCommand,
}: {
  npcId: number;
  shop: ShopStockPacket | null;
  items: readonly ItemDefinitionWire[];
  /** For a line kept for whoever has finished something. */
  quests: readonly QuestWire[];
  characters: readonly { id: number; name: string }[];
  objects: readonly { id: number; name: string }[];
  onCommand: AdminCommand;
}): JSX.Element | null {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(0);

  // Only the shop this character keeps, so a panel left open on somebody else
  // does not offer to edit the wrong one.
  const held = shop && shop.npcId === npcId ? shop : null;
  if (!held) return null;

  const nameOf = (itemId: number): string =>
    items.find((item) => item.id === itemId)?.name ?? `Item ${itemId}`;

  const wanted = search.trim().toLowerCase();
  const matches = wanted
    ? items.filter((item) => item.name.toLowerCase().includes(wanted)).slice(0, 6)
    : [];

  return (
    <div className="editor-shop">
      <h3>{held.name}</h3>

      <div className="editor-fact">
        <span>Buy Price</span>
        <span>{Math.round(held.buyRate * 100)}% of value</span>
      </div>
      <div className="editor-fact">
        <span>Sell Price</span>
        <span>{Math.round(held.sellRate * 100)}% of value</span>
      </div>

      <Slider
        label="Buy Price"
        value={Math.round(held.buyRate * 100)}
        min={10}
        max={400}
        step={5}
        suffix="%"
        onChange={(value) => onCommand('setShop', { shop: held.id, buyRate: value / 100 })}
      />
      <Slider
        label="Sell Price"
        value={Math.round(held.sellRate * 100)}
        min={0}
        max={200}
        step={5}
        suffix="%"
        onChange={(value) => onCommand('setShop', { shop: held.id, sellRate: value / 100 })}
      />
      <Slider
        label="Restock"
        value={held.restockTicks}
        min={5}
        max={1000}
        step={5}
        suffix=" ticks"
        onChange={(value) => onCommand('setShop', { shop: held.id, restockTicks: value })}
      />

      {held.stock.map((line) => (
        <div key={line.itemId}>
          <div className="editor-drop">
            <span className="editor-drop-name">{nameOf(line.itemId)}</span>
            <span className="editor-drop-count">{line.quantity}</span>
            <span className="editor-drop-times">
              <button
                type="button"
                className="editor-step"
                title="One fewer"
                onClick={() =>
                  onCommand('setShop', {
                    shop: held.id,
                    item: line.itemId,
                    quantity: Math.max(0, line.quantity - 1),
                  })
                }
              >
                −
              </button>
              <button
                type="button"
                className="editor-step"
                title="One more"
                onClick={() =>
                  onCommand('setShop', {
                    shop: held.id,
                    item: line.itemId,
                    quantity: line.quantity + 1,
                  })
                }
              >
                +
              </button>
            </span>
            <button
              type="button"
              className={open === line.itemId ? 'editor-tab on' : 'editor-tab'}
              title="Who this line is for"
              onClick={() => setOpen(open === line.itemId ? 0 : line.itemId)}
            >
              {whenOf(line.when).length > 0 ? 'Kept for' : 'Anybody'}
            </button>
            <button
              type="button"
              className="editor-drop-remove"
              title="Stop selling it"
              onClick={() =>
                onCommand('setShop', { shop: held.id, item: line.itemId, quantity: 0 })
              }
            >
              ✕
            </button>
          </div>

          {open === line.itemId ? (
            <Conditions
              held={whenOf(line.when)}
              items={items}
              quests={quests}
              characters={characters}
              objects={objects}
              onChange={(when) =>
                onCommand(
                  'setShopWhen',
                  { shop: held.id, item: line.itemId },
                  { json: JSON.stringify(when) },
                )
              }
            />
          ) : null}
        </div>
      ))}

      {held.stock.length === 0 ? <p className="hint">It sells nothing at all.</p> : null}

      <div className="editor-drop-add">
        <input
          className="editor-search"
          type="text"
          value={search}
          placeholder="Sell something else"
          onChange={(event) => setSearch(event.target.value)}
        />

        {matches.map((item) => (
          <div className="editor-drop-match" key={item.id}>
            <span>{item.name}</span>
            <button
              type="button"
              onClick={() => {
                onCommand('setShop', { shop: held.id, item: item.id, quantity: 5 });
                setSearch('');
              }}
            >
              Keep five
            </button>
          </div>
        ))}

        <button type="button" className="wide" onClick={() => onCommand('saveShops')}>
          Save the shops
        </button>
      </div>
    </div>
  );
}
